import {
  DEFAULT_PLATFORM_THEME_PREFERENCE,
  PLATFORM_THEME_COOKIE_MAX_AGE_SECONDS,
  PLATFORM_THEME_COOKIE_NAME,
  PLATFORM_THEME_SHARED_COOKIE_DOMAIN,
  PLATFORM_THEME_SHARED_COOKIE_HOSTS,
  isPlatformThemePreference,
  serializePlatformThemePreferenceCookie,
  type PlatformThemePreference,
} from "@kaleo/account-localization";

export const ZUDOKU_THEME_STORAGE_KEY = "theme";

type BrowserLocation = {
  hostname?: string;
  protocol?: string;
};

type CookieDocument = {
  cookie: string;
};

export function readPlatformThemePreference(
  cookieHeader: string,
): PlatformThemePreference | null {
  for (const part of cookieHeader.split(";")) {
    const separator = part.indexOf("=");
    if (
      separator === -1 ||
      part.slice(0, separator).trim() !== PLATFORM_THEME_COOKIE_NAME
    ) {
      continue;
    }

    try {
      const value = decodeURIComponent(part.slice(separator + 1).trim());
      if (isPlatformThemePreference(value)) return value;
    } catch {
      // A duplicate valid domain cookie may still follow a malformed value.
    }
  }

  return null;
}

export function saveDocsThemePreference(
  preference: PlatformThemePreference,
  cookieDocument: CookieDocument = globalThis.document,
  location: BrowserLocation = globalThis.location,
) {
  cookieDocument.cookie = serializePlatformThemePreferenceCookie(
    preference,
    location,
  );
}

export function shouldSaveDocsThemePreference(
  preference: PlatformThemePreference,
  cookieHeader: string,
): boolean {
  const persistedPreference = readPlatformThemePreference(cookieHeader);
  return (
    persistedPreference !== preference &&
    (persistedPreference !== null ||
      preference !== DEFAULT_PLATFORM_THEME_PREFERENCE)
  );
}

const bootstrapSettings = JSON.stringify({
  cookieName: PLATFORM_THEME_COOKIE_NAME,
  defaultPreference: DEFAULT_PLATFORM_THEME_PREFERENCE,
  maxAge: PLATFORM_THEME_COOKIE_MAX_AGE_SECONDS,
  sharedCookieDomain: PLATFORM_THEME_SHARED_COOKIE_DOMAIN,
  sharedCookieHosts: PLATFORM_THEME_SHARED_COOKIE_HOSTS,
  storageKey: ZUDOKU_THEME_STORAGE_KEY,
});

export const DOCS_THEME_BOOTSTRAP_SCRIPT = `(() => {
  const settings = ${bootstrapSettings};
  const isPreference = (value) => value === "light" || value === "dark";
  let cookiePreference = null;

  for (const part of document.cookie.split(";")) {
    const separator = part.indexOf("=");
    if (
      separator === -1 ||
      part.slice(0, separator).trim() !== settings.cookieName
    ) continue;

    try {
      const value = decodeURIComponent(part.slice(separator + 1).trim());
      if (isPreference(value)) {
        cookiePreference = value;
        break;
      }
    } catch {
      // A duplicate valid domain cookie may still follow a malformed value.
    }
  }

  let preference = cookiePreference ?? settings.defaultPreference;
  let storedPreference = null;
  try {
    const value = localStorage.getItem(settings.storageKey);
    storedPreference = isPreference(value) ? value : null;
    if (!cookiePreference && isPreference(storedPreference)) {
      preference = storedPreference;
    }
    localStorage.setItem(settings.storageKey, preference);
  } catch {}

  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(preference);
  root.style.colorScheme = preference;

  if (!cookiePreference && storedPreference) {
    const isHttps = location.protocol === "https:";
    const sharesProductionDomain =
      isHttps &&
      settings.sharedCookieHosts.includes(location.hostname);
    const attributes = [
      settings.cookieName + "=" + preference,
      "Max-Age=" + settings.maxAge,
      "Path=/",
      "SameSite=Lax",
    ];
    if (sharesProductionDomain) {
      attributes.push("Domain=" + settings.sharedCookieDomain);
    }
    if (isHttps) attributes.push("Secure");
    document.cookie = attributes.join("; ");
  }
})();`;
